import React, { useEffect, useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { httpRequest } from "../../utils/httpRequest";
import Pagination from "../../components/organization/Pagination";
import ResourcesTable from "../../components/resources/ResourcesTable";
import CreateResourceModal from "../../components/resources/CreateResourceModal";
import EditResourceModal from "../../components/resources/EditResourceModal";
import { toast } from "react-toastify";

export default function OrgResourcesPage() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");
  const token = JSON.parse(localStorage.getItem("user"))?.token;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [limit] = useState(20);

  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const fetchData = async (p = page) => {
    setLoading(true);
    try {
      const { data } = await httpRequest(
        "GET",
        "/resource",
        { page: p, limit },
        { "x-access-token": token }
      );

      const payload = data?.data || {};
      setItems(payload.items || []);
      setPage(payload.page || p);
      setPages(payload.pages || 1);
    } catch (e) {
      setItems([]);
      setPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  const handleDelete = async (id) => {
    if (!token) return;
    try {
      await httpRequest("DELETE", `/resource/${id}`, {}, { "x-access-token": token });
      toast.success(t("common.deleted", "حذف شد"));
      fetchData(page);
    } catch (e) {
      toast.error(e?.message || t("common.unknown_error"));
    }
  };

  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  }`;

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("resources.admin_page_title", "مدیریت منابع آموزشی")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-800">
              {t("resources.admin_page_header", "منابع آموزشی")}
            </h1>

            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus size={18} /> {t("resources.create", "ایجاد منبع")}
            </button>
          </div>

          <ResourcesTable
            items={items}
            loading={loading}
            isRtl={isRtl}
            onEdit={(r) => setEditItem(r)}
            onDelete={(id) => handleDelete(id)}
          />

          <Pagination
            page={page}
            pages={pages}
            onChange={(p) => {
              setPage(p);
              fetchData(p);
            }}
          />
        </main>
      </div>

      <CreateResourceModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          fetchData(1);
        }}
      />

      <EditResourceModal
        isOpen={!!editItem}
        onClose={() => setEditItem(null)}
        resource={editItem}
        onSaved={() => {
          setEditItem(null);
          fetchData(page);
        }}
      />
    </div>
  );
}
